import { useMemo, useState } from "react";
import { ChangeAlertCallbackType } from "../../../Root";
import { useAFBFunction } from "./useAFBFunction";
import { useAFBActionExecute } from "./useAFBActionsExecute";
import { status, afbActions } from "../../../../util/DataObject";
import { CustomMenu } from "../../../elements";
import { Settings, ContactPageSharp, ForwardToInbox, MarkEmailRead, AdminPanelSettings, LeakRemove, AdbOutlined, QrCodeScanner } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useConfirm } from "../../../modals/confirm";
import moment from "moment";
moment.locale("es");

const Icons: Record<string, JSX.Element> = {
    ContactPageSharp: <ContactPageSharp/>,
    ForwardToInbox: <ForwardToInbox/>,
    MarkEmailRead: <MarkEmailRead/>,
    AdminPanelSettings: <AdminPanelSettings/>,
    LeakRemove: <LeakRemove/>,
    AdbOutlined: <AdbOutlined/>,
    QrCodeScanner: <QrCodeScanner/>,
};

export const useAFBAdminTable = (changeAlert: ChangeAlertCallbackType, config) => {
    const changePage = (action, page, rows = rowsPerPage) => {
        setPage(page);
        getData(page, rows, company ? {company_uuid: company} : null);
    };

    const { handleAdminList, handleAdminCreate, handleAdminUpdate } = useAFBFunction(changeAlert);
    const { loading: loadingExecute, item, element, dialogText, dialogProps, otpTokenGetAllowed, dataReuse, onOpenConfirm, onHandleConfirm, onCloseDialogConfirm, onInsertAlboToken } = useAFBActionExecute(changeAlert, changePage);
    const [afbNewActions, setAfbNewActions] = useState([]);

    const [data, setData] = useState([]);
    const [admin, setAdmin] = useState(null);
    const [action, setAction] = useState(null);
    const [loading, setLoading] = useState(false);
    const [company, setCompany] = useState(null);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalDocuments, setTotalDocuments] = useState(0);

    const { DialogConfirm, confirm: confirmUpdateStatus } = useConfirm(
        'Actualizar administrador',
        '¿Estás seguro que deseas realizar este cambio? Recuerda que el cambio de correo y teléfono sólo se podrá realizar dos veces al mes'
    );

    const saveData = async (data) => {
        setData(JSON.parse(JSON.stringify(data.content || [])));
        setTotalDocuments(data["content"]["count"] || 0);

        if(afbNewActions.length === 0){
            const a = afbActions.filter(opt => hasAction(opt["key"]))
            const b = a.map((i) => ({
                ...i,
                icon: Icons[i["icon"]] || <Settings />
            }));
            setAfbNewActions(b);
        }
        data["content"]["uuid"] && setCompany(data["content"]["uuid"]);
    };

    const getData = async (newPage = page, newPerPage = rowsPerPage, filter = null) => {
        setLoading(true);
        let payload = {
            filter: filter || {},
            offset: newPage * newPerPage,
            limit: newPerPage,
        };

        let resp = await handleAdminList(payload);
        if (resp) {
            saveData(resp);
        } else {
            setData([]);
            setTotalDocuments(0);
        }
        setLoading(false);
    };

    const onHandleSearch = async ({value}) => {
        let filter = value ? {email: value} : {};
        setPage(0);
        getData(0, rowsPerPage, filter);
    };
    
    
    const onOpenAdminCreate = async () => {
        return actionExecute({}, "createAdmin");
    };

    const onHandleCreate = async (payload) => {
        setLoading(true);

        let resp = await handleAdminCreate(payload);
        if (resp) {
            changeAlert(true, "success", "Usuario creado con éxito.");
            setLoading(false);
            await hasAction("insertAlboToken") && await onInsertAlboToken(resp);
            onHandleSearch({value: payload.email});
            onHandleWidgetChange();
        } else {
            setLoading(false);
        }
    };

    const onHandleUpdate = async (form, confirm) => {
        let isConfirm = confirm;
        if (!confirm) {
            isConfirm = await confirmUpdateStatus();
        }

        if (isConfirm) {
            setLoading(true);
            let resp = await handleAdminUpdate(form);
            if (resp) {
                changeAlert(true, "success", "Usuario editado con éxito.");
                setLoading(false);
                onHandleSearch({value: form.email});
                onHandleWidgetChange();
            } else {
                setLoading(false);
            }
        }
    };

    const onHandleUpdateStatus = async (item, e) => {
        setLoading(true);

        let payload = item;
        payload["status"] = e.target.value;
        delete payload["uuid"];
        payload["email"] = item.admin.email;

        let resp = await handleAdminUpdate(payload);
        if (resp) {
            await changePage(null, page);
            changeAlert(true, "success", "El estatus fue actualizado exitosamente.");
            setLoading(false);
        } else {
            setLoading(false);
        }
    };

    const onOpenDetail = (customer, action, listActions) => {
        setAction(JSON.parse(JSON.stringify(action)));
        setAdmin(JSON.parse(JSON.stringify(customer)));
        setAfbNewActions(listActions);
    };

    const onHandleWidgetChange = () => {
        setAction(JSON.parse(JSON.stringify(null)));
        setAdmin(JSON.parse(JSON.stringify(null)));
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setRowsPerPage(JSON.parse(JSON.stringify(event.target.value)));
        changePage(null, 0, parseInt(event.target.value, 10));
    };

    const actionExecute = (row, key) => {
        switch (key) {
            case "otpTokenGetAllowed":
                return otpTokenGetAllowed(row, 'otpTokenDisabledConfirm');
            case "dataReuseAfb":
                return dataReuse(row.uuid);
            case "editAdmin":
                return onOpenDetail(row, "edit", afbNewActions);
            case "createAdmin":
                return onOpenDetail({}, "create", afbNewActions);
            default:
                return onOpenConfirm(row, key);
        } 
    };

    const validateStatus = (row, list) => {
        let status = list.filter((i) => {
            return i.status ? i.status === row.status ? i.validation ? !row.admin.email.includes('_REUSADO') : i : null : i
        })
        return status;
    };

    const calculateColumns = useMemo(() => {
        const columns = [
            { cell: (row) => {
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    return <a href="#" onClick={onOpenDetail.bind(this, row, "detail", afbNewActions)} title="Ver más"><VisibilityIcon /></a>
                }, width: "4%", sortable: true
            },
            { selector: (row) => `${row.admin.gname} ${row.admin.lname} ${row.admin.slname}`, name: "Nombre",  wrap: true, sortable: true },
            { selector: (row) => row.admin.email, name: "Email",  wrap: true, sortable: true },
            { selector: (row) => row.admin.phone, name: "Teléfono", width:"12%", wrap: true, sortable: true },
            { selector: (row) => row.company.length, name: "Empresas", width: "10%", wrap: true, sortable: true, center: true },
            { cell: (row) => {
                return (
                    <select value={row.status} onChange={onHandleUpdateStatus.bind(this, row)} disabled={row.status === "disabled" ? true : false}>
                    { status.map((item, index) => {
                            return <option key={`state-${index}`} value={item.value}>
                                {item.label}
                            </option>
                        })
                    }
                    </select>
                )}, name: "Estatus", wrap: true, sortable: true, width: "15%"
            },
            { selector: (row) => moment(new Date(row.cdate)).format("L"), name: "Fecha creación", wrap: true, sortable: true, width: "12%" },
            { cell: (row) => {
                return <CustomMenu width={4} size="small" row={row} options={validateStatus(row, afbNewActions)} action={actionExecute} icon={<MoreHorizIcon/>}/>
            }, width: "5%", sortable: true}
        ];

        return columns;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [afbNewActions]);

    const hasAction = (action) => {
        return config?.actions?.indexOf(action) > -1;
    };

    return {
        data,
        admin,
        action,
        loading,
        loadingExecute,
        afbNewActions,
        columns: calculateColumns,
        pagination: { page, rowsPerPage, totalDocuments },
        item, element, dialogText, dialogProps,
        saveData,
        changePage,
        handleChangeRowsPerPage,
        onOpenAdminCreate,
        onOpenDetail,
        onHandleSearch,
        onHandleWidgetChange,
        onHandleUpdate,
        onHandleUpdateStatus,
        onHandleCreate,
        onHandleConfirm, 
        DialogConfirm,
        onCloseDialogConfirm,
        otpTokenGetAllowed, dataReuse, onOpenConfirm
    };
};
